import React, { useContext, useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Select,
  Switch,
  Typography,
  Grid,
  InputAdornment,
  CircularProgress
} from '@material-ui/core';
import ArchiveOutlinedIcon from '@material-ui/icons/ArchiveOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import MoneyOutlinedIcon from '@material-ui/icons/MoneyOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AutocompleteInput from 'components/CustomInputs/AutocompleteInput';
import { I18nContext } from 'translations';
import { COUNTRY_CODE, SIZE_CD, PAYMENT_METHODS_PIBOX } from 'utils/constants';
import { withStyles } from '@material-ui/core/styles';
import styles from './FormStyle';
import Money from 'utils/money';
import { formatCostCompact } from 'utils';
import { Paper, IconButton, Box } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import ModalListFavoriteAddresses from 'components/Modals/Settings/FavoriteAddresses/ListFavoriteAddress';
import { favoriteToStop } from 'utils/favoriteAddress';
import { Field, ColumnTitle, Stepper, FavoriteAddressButton } from './Parts';

const EMPTY_STOP = { name: null, lat: null, lon: null };
const STEP_PACKAGE = 'package';

// Visibilidad de las opciones de cobro del formulario (columna "Pago y cobros").
// Están ocultas por solicitud del negocio. Para volver a mostrar alguna, cambiar su flag a `true`:
//   SHOW_COLLECT_ON_DELIVERY -> "Recaudar al entregar" (counter_delivery / collected_value)
//   SHOW_PAY_ON_PICKUP       -> "Pagar al recoger" (requires_a_driver_with_base_money / money_to_pay_for_package)
// La nota "El valor declarado no es el costo del envío." se muestra si cualquiera de las dos está visible.
// Mientras estén ocultas, sus campos no se registran en el formulario y no se envían.
const SHOW_COLLECT_ON_DELIVERY = false;
const SHOW_PAY_ON_PICKUP = false;

const QuoteStrip = ({ quoteResult, translate, currency, classes, disabled, onQuote }) => (
  <>
    {quoteResult?.error && <Alert severity="error" style={{ borderRadius: 0 }}>{quoteResult.error}</Alert>}
    <div className={classes.quoteStrip}>
      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        <LocalShippingOutlinedIcon style={{ color: '#2e7d32' }} />
        <div>
          <Typography variant="caption" color="textSecondary" display="block">
            {translate('prepackages.form.quote_cost')}
          </Typography>
          {quoteResult?.loading ? (
            <CircularProgress size={18} style={{ color: '#2e7d32' }} />
          ) : (
            <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.2 }}>
              {quoteResult?.fare ? formatCostCompact(quoteResult.fare) : translate('prepackages.form.pendingQuote')}
              <Typography component="span" variant="caption" color="textSecondary"> · {currency}</Typography>
            </Typography>
          )}
        </div>
      </Box>
      <Button variant="outlined" size="large" disabled={disabled} onClick={onQuote}>
        {translate('prepackages.form.quote')}
      </Button>
    </div>
  </>
);

// Read-only recap that fills the payment column: route, package and the quoted cost.
const OrderSummary = ({ classes, translate, currency, origin, destination, sizeLabel, declaredValue, quoteResult }) => {
  const rows = [
    [translate('prepackages.form.summaryOrigin'), origin?.name],
    [translate('prepackages.form.summaryDestination'), destination?.name],
    [translate('prepackages.form.size'), sizeLabel],
    [translate('prepackages.form.declaredValue'), declaredValue ? `$${declaredValue} ${currency}` : null]
  ];
  return (
    <Paper elevation={0} style={{ border: '1px solid #E6E6EB', borderRadius: 12, background: '#FAFAFC', padding: 16, marginTop: 8 }}>
      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>
        {translate('prepackages.form.summaryTitle')}
      </Typography>
      {rows.map(([label, value]) => (
        <Box key={label} display="flex" justifyContent="space-between" style={{ gap: 16, marginBottom: 8 }}>
          <Typography variant="caption" color="textSecondary">{label}</Typography>
          <Typography variant="caption" style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
            {value || '—'}
          </Typography>
        </Box>
      ))}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ borderTop: '1px solid #E6E6EB', paddingTop: 12, marginTop: 4 }}>
        <Typography variant="caption" color="textSecondary">{translate('prepackages.form.quote_cost')}</Typography>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {quoteResult?.fare ? `${formatCostCompact(quoteResult.fare)} ${currency}` : translate('prepackages.form.pendingQuote')}
        </Typography>
      </Box>
    </Paper>
  );
};

// A switch row that reveals its amount field only while it is on.
const ToggleRow = ({ classes, title, description, checked, onChange, name, inputRef, children }) => (
  <div style={{ borderTop: '1px solid #E6E6EB', paddingTop: 20, marginBottom: 20 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{title}</Typography>
      <Switch
        color="primary"
        name={name}
        inputRef={inputRef}
        defaultChecked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </Box>
    <div className={classes.hint} style={{ marginTop: 0, marginBottom: 16 }}>{description}</div>
    {children}
  </div>
);

function PackagesGroupingForm ({
  register,
  setValue,
  watch,
  address,
  origin,
  submit,
  quote,
  quoteResult,
  city,
  handleAddress,
  handleSuggest,
  cities,
  handleSubmit,
  classes,
  errors,
  isValid,
  setExpanded,
  expanded,
  isModal,
  prepackage
}) {
  const { langCode } = useContext(I18nContext);
  const [checkedDelivery, setCheckedDelivery] = useState(prepackage?.counter_delivery || false);
  const [checkedDriverPays, setCheckedDriverPays] = useState(prepackage?.requires_a_driver_with_base_money || false);
  const { translate } = useContext(I18nContext);
  const currency = COUNTRY_CODE[langCode]?.[4];
  // The API answers with the RESOLVED contact: only the one typed on the service is editable here,
  // or saving would turn the company-profile number into the service's own.
  const serviceOriginContact = prepackage?.origin_contact?.source === 'service' ? prepackage.origin_contact : null;
  const onPackageStep = expanded === STEP_PACKAGE;
  const destination = address && address[0]?.name ? address[0] : null;
  const sizeCd = watch?.('size_cd');
  const declaredValue = watch?.('declared_value');

  useEffect(() => {
    expanded === 'resetCity' && setCheckedDelivery(false);
    expanded === 'resetCity' && setCheckedDriverPays(false);
  }, [expanded])

  // "Limpiar" empties step 1 only: both addresses, their complements and both contacts.
  const clearAddresses = () => {
    handleSuggest(null);
    handleAddress(EMPTY_STOP, 0, 0);
    ['origin_secondary_addres', 'end_secondary_address', 'origin_contact.name', 'origin_contact.phone',
      'customer.name', 'customer.phone', 'customer.email'].forEach((name) => setValue(name, ''));
  };

  // Each favorite icon loads the chosen address only into the field that opened the list.
  const [favoriteTarget, setFavoriteTarget] = useState(null);
  const loadFavoriteInto = {
    origin: (stop) => handleSuggest(stop, 0, 0),
    destination: (stop) => handleAddress(stop, 0, 0)
  };
  const selectFavorite = (favorite) => {
    const stop = favoriteToStop(favorite);
    stop && loadFavoriteInto[favoriteTarget](stop);
    setFavoriteTarget(null);
  };

  const [showDisclaimer, setShowDisclaimer] = useState(true);

  const OptimizedDeliveryDisclaimer = () =>
      !showDisclaimer ? null : (
          <Paper
              elevation={0}
              style={{
                border: '1px solid #E9E6FF',
                borderRadius: 12,
                background: '#FAF8FF',
                margin: '0 20px 16px 10px', // mismo margen lateral que el form padding
                padding: 16
              }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Typography
                  variant="h6"
                  style={{ color: '#7825BD', fontWeight: 800, marginBottom: 8 }}
              >
                ¿Qué es Entrega Optimizada?
              </Typography>
              <IconButton
                  size="small"
                  aria-label="Cerrar"
                  onClick={() => setShowDisclaimer(false)}
                  style={{ marginLeft: 8 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="body1" style={{ lineHeight: 1.6, marginBottom: 8 }}>
              Es una <b>modalidad de envío donde tu paquete viaja junto con los de otros
              clientes</b> en una ruta optimizada.
            </Typography>

            <Typography variant="body1" style={{ lineHeight: 1.6 }}>
              Esto nos permite reducir costos, hacer más entregas en menos tiempo y
              cuidar el medio ambiente. <b>Tu envío será entregado el mismo día, pero
              el orden de la recolección o de la entrega puede variar según la ruta planificada.</b>
            </Typography>

            <Typography variant="caption" display="block" style={{ marginTop: 12, opacity: 0.9 }}>
              *Este servicio depende de la disponibilidad de otros envíos generados en tu zona que
              permitan consolidar la ruta.
            </Typography>
          </Paper>
      );

  const cityId = city ? city._id : cities[0]._id;
  const pos = { lat: city ? city.location?.lat : cities[0].lat, lon: city ? city.location?.lon : cities[0].lon };
  const errorText = (path) => errors && path && translate(path.message);

  return (
    <>
      <OptimizedDeliveryDisclaimer />
      <Stepper
        steps={[translate('prepackages.form.addresses'), translate('prepackages.form.step.package')]}
        current={onPackageStep ? 1 : 0}
      />
      <form autoComplete="off" noValidate onSubmit={handleSubmit(submit)}>
        {/* Both steps stay mounted: react-hook-form drops the values of inputs that unmount. */}
        <div className={classes.card} style={{ display: onPackageStep ? 'none' : 'block' }}>
          <div className={classes.cardHeader}>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700 }}>{translate('prepackages.form.addressesTitle')}</Typography>
              <Typography variant="body2" color="textSecondary">{translate('prepackages.form.addressesSubtitle')}</Typography>
            </div>
            <Typography variant="caption" color="textSecondary">
              <span className={classes.required}>*</span> {translate('prepackages.form.requiredFields')}
            </Typography>
          </div>
          <Grid container>
            <Grid item xs={12} md={6} className={classes.column}>
              <ColumnTitle
                classes={classes}
                icon={<ArchiveOutlinedIcon />}
                color="#0E7C5A"
                background="#DDF3EA"
                title={translate('prepackages.form.origin')}
                subtitle={translate('prepackages.form.originSubtitle')}
              />
              <Field classes={classes} label={translate('prepackages.form.pickupAddress')} required>
                <Box display="flex" alignItems="center">
                  <Box flex={1}>
                    <AutocompleteInput
                      title={translate('prepackages.form.originAddress')}
                      name="origin"
                      address={origin && origin}
                      cityId={cityId}
                      pos={pos}
                      focus
                      hideLabel
                      handleChange={(info) => handleSuggest(info, 0, 0)}
                    />
                  </Box>
                  <FavoriteAddressButton
                    label={translate('prepackages.form.favoriteAddresses')}
                    onClick={() => setFavoriteTarget('origin')}
                  />
                </Box>
              </Field>
              <Field
                classes={classes}
                label={translate('prepackages.form.complementaryAddress')}
                hint={translate('prepackages.form.originComplementHint')}
              >
                <TextField
                  type="text"
                  name="origin_secondary_addres"
                  variant="outlined"
                  placeholder={translate('prepackages.form.complementPlaceholder')}
                  fullWidth
                  defaultValue={prepackage?.origin_secondary_address}
                  inputRef={register}
                  size="small"
                />
              </Field>

              <div className={classes.sectionDivider} />
              <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{translate('prepackages.form.personWhoDispatches')}</Typography>
              </Box>
              <Field classes={classes} label={translate('prepackages.form.fullName')}>
                <TextField
                  name="origin_contact.name"
                  defaultValue={serviceOriginContact?.name}
                  inputRef={register}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Field>
              <Field
                classes={classes}
                label={translate('prepackages.form.originPhone')}
                hint={translate('prepackages.form.originPhoneHint')}
                error={errorText(errors?.origin_contact?.phone)}
              >
                <TextField
                  name="origin_contact.country_code"
                  inputRef={register}
                  type="hidden"
                  defaultValue={serviceOriginContact?.country_code || COUNTRY_CODE[langCode][3]}
                />
                <TextField
                  name="origin_contact.phone"
                  defaultValue={serviceOriginContact?.phone}
                  inputRef={register}
                  inputProps={{ maxLength: '11' }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors?.origin_contact?.phone}
                />
              </Field>
            </Grid>

            <Grid item xs={12} md={6} className={classes.column}>
              <ColumnTitle
                classes={classes}
                icon={<LocationOnOutlinedIcon />}
                color="#6A2CC0"
                background="#ECE6FA"
                title={translate('prepackages.form.destination')}
                subtitle={translate('prepackages.form.destinationSubtitle')}
              />
              <Field classes={classes} label={translate('prepackages.form.deliveryAddress')} required>
                <Box display="flex" alignItems="center">
                  <Box flex={1}>
                    <AutocompleteInput
                      title={translate('prepakages.form.destinationAddress')}
                      name="address"
                      address={destination}
                      cityId={cityId}
                      pos={pos}
                      focus={false}
                      hideLabel
                      handleChange={(info) => handleAddress(info, 0, 0)}
                    />
                  </Box>
                  <FavoriteAddressButton
                    label={translate('prepackages.form.favoriteAddresses')}
                    onClick={() => setFavoriteTarget('destination')}
                  />
                </Box>
              </Field>
              <Field
                classes={classes}
                label={translate('prepackages.form.complementaryAddress')}
                hint={translate('prepackages.form.destinationComplementHint')}
              >
                <TextField
                  name="end_secondary_address"
                  placeholder={translate('prepackages.form.complementPlaceholder')}
                  defaultValue={prepackage?.end_secondary_address}
                  inputRef={register}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Field>

              <div className={classes.sectionDivider} />
              <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{translate('prepackages.form.personWhoReceives')}</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <Field classes={classes} label={translate('prepackages.form.fullName')}>
                    <TextField
                      name="customer.name"
                      defaultValue={prepackage?.customer_info?.name}
                      inputRef={register}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Field
                    classes={classes}
                    label={translate('prepackages.form.receiverPhone')}
                    error={errorText(errors?.customer?.phone)}
                  >
                    <TextField
                      name="customer.country_code"
                      inputRef={register}
                      type="hidden"
                      defaultValue={prepackage?.customer_info?.country_code || COUNTRY_CODE[langCode][3]}
                    />
                    <TextField
                      name="customer.phone"
                      defaultValue={prepackage?.customer_info?.phone}
                      inputRef={register}
                      inputProps={{ maxLength: '11' }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={!!errors?.customer?.phone}
                    />
                  </Field>
                </Grid>
              </Grid>
              <Field
                classes={classes}
                label={translate('prepackages.form.customer.email')}
                hint={translate('prepackages.form.emailHint')}
                error={errorText(errors?.customer?.email)}
              >
                <TextField
                  name="customer.email"
                  placeholder={translate('prepackages.form.emailPlaceholder')}
                  defaultValue={prepackage?.customer_info?.email}
                  inputRef={register}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors?.customer?.email}
                />
              </Field>
            </Grid>
          </Grid>
          <div className={classes.footer}>
            <Box display="flex" alignItems="center" style={{ gap: 8, color: '#666' }}>
              <VerifiedUserOutlinedIcon fontSize="small" />
              <Typography variant="caption">{translate('prepackages.form.privacy')}</Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              {!isModal && (
                <Button type="button" onClick={clearAddresses} style={{ color: '#666', fontWeight: 600 }}>
                  {translate('prepackages.form.clear')}
                </Button>
              )}
              <Button
                type="button"
                color="primary"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                disabled={!origin?.lat || !address[0]?.lat}
                onClick={(e) => {
                  e.preventDefault();
                  setExpanded(STEP_PACKAGE);
                }}
              >
                {translate('prepackages.form.continueToPackage')}
              </Button>
            </Box>
          </div>
        </div>

        {expanded === 'resetCity' ? null : (
          <div className={classes.card} style={{ display: onPackageStep ? 'block' : 'none' }}>
            <div className={classes.cardHeader}>
              <div>
                <Typography variant="h6" style={{ fontWeight: 700 }}>{translate('prepackages.form.packageAndPaymentTitle')}</Typography>
                <Typography variant="body2" color="textSecondary">{translate('prepackages.form.packageAndPaymentSubtitle')}</Typography>
              </div>
              <Typography variant="caption" color="textSecondary">
                <span className={classes.required}>*</span> {translate('prepackages.form.requiredFields')}
              </Typography>
            </div>
            <Grid container>
              <Grid item xs={12} md={6} className={classes.column}>
                <ColumnTitle
                  classes={classes}
                  icon={<ArchiveOutlinedIcon />}
                  color="#6A2CC0"
                  background="#ECE6FA"
                  title={translate('prepackages.form.packageInfo')}
                  subtitle={translate('prepackages.form.packageInfoSubtitle')}
                />
                <Field
                  classes={classes}
                  label={translate('prepackages.form.WhatTheCourierDo')}
                  required
                  error={errors?.indications && translate(errors.indications.message)}
                >
                  <TextField
                    name="indications"
                    placeholder={translate('prepackages.form.instructionsPlaceholder')}
                    defaultValue={prepackage?.indications}
                    inputRef={register}
                    fullWidth
                    variant="outlined"
                    size="small"
                    multiline
                    rows={4}
                    error={!!errors?.indications}
                  />
                </Field>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field classes={classes} label={translate('prepackages.form.size')}>
                      <Select
                        native
                        fullWidth
                        variant="outlined"
                        defaultValue={prepackage?.size_cd || 0}
                        inputRef={register}
                        name="size_cd"
                        margin="dense"
                      >
                        {Object.values(SIZE_CD).map((size, index) => (
                          <option key={index.toString()} value={index}>
                            {translate(size)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      classes={classes}
                      label={translate('prepackages.form.declaredValue')}
                      required
                      error={errors?.declared_value && translate(errors.declared_value.message)}
                    >
                      <TextField
                        name="declared_value"
                        defaultValue={prepackage?.declared_value && new Money(prepackage?.declared_value).flatFormat()}
                        inputRef={register}
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={!!errors?.declared_value}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          endAdornment: <InputAdornment position="end">{currency}</InputAdornment>
                        }}
                      />
                    </Field>
                  </Grid>
                </Grid>
                <Field
                  classes={classes}
                  label={translate('prepackages.form.orderNumber')}
                  required
                  error={errors?.reference && translate(errors.reference.message)}
                >
                  <TextField
                    name="reference"
                    placeholder={translate('prepackages.form.orderNumberPlaceholder')}
                    defaultValue={prepackage?.reference}
                    inputRef={register}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!errors?.reference}
                  />
                </Field>
              </Grid>

              <Grid item xs={12} md={6} className={classes.column}>
                <ColumnTitle
                  classes={classes}
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  color="#0E7C5A"
                  background="#DDF3EA"
                  title={translate('prepackages.form.paymentAndCollections')}
                  subtitle={translate('prepackages.form.paymentAndCollectionsSubtitle')}
                />
                <Field classes={classes} label={translate('prepackages.form.paymentMethod')} required>
                  <Select
                    native
                    fullWidth
                    variant="outlined"
                    defaultValue={prepackage?.payment_method_cd || 1}
                    inputRef={register}
                    name="payment_method_cd"
                    margin="dense"
                  >
                    {Object.entries(PAYMENT_METHODS_PIBOX).map(([key, val]) => (
                      <option key={key} value={key}>{translate(val)}</option>
                    ))}
                  </Select>
                </Field>

                {SHOW_COLLECT_ON_DELIVERY && (
                  <ToggleRow
                    classes={classes}
                    title={translate('prepackages.form.collectOnDelivery')}
                    description={translate('prepackages.form.collectOnDeliveryDesc')}
                    name="counter_delivery"
                    inputRef={register}
                    checked={prepackage?.counter_delivery || false}
                    onChange={setCheckedDelivery}
                  >
                    {checkedDelivery && (
                      <Field
                        classes={classes}
                        label={`${translate('prepackages.form.valueToCollect')} (${currency})`}
                        required
                        error={errors?.collected_value && translate(errors.collected_value.message)}
                      >
                        <TextField
                          name="collected_value"
                          defaultValue={prepackage?.collected_value && new Money(prepackage?.collected_value).flatFormat()}
                          inputRef={register}
                          fullWidth
                          variant="outlined"
                          size="small"
                          error={!!errors?.collected_value}
                        />
                      </Field>
                    )}
                  </ToggleRow>
                )}

                {SHOW_PAY_ON_PICKUP && (
                  <ToggleRow
                    classes={classes}
                    title={translate('prepackages.form.payOnPickup')}
                    description={translate('prepackages.form.payOnPickupDesc')}
                    name="requires_a_driver_with_base_money"
                    inputRef={register}
                    checked={prepackage?.requires_a_driver_with_base_money || false}
                    onChange={setCheckedDriverPays}
                  >
                    {checkedDriverPays && (
                      <Field
                        classes={classes}
                        label={`${translate('prepackages.form.moneyToPayForPackage')} (${currency})`}
                        required
                      >
                        <TextField
                          name="money_to_pay_for_package"
                          defaultValue={prepackage?.money_to_pay_for_package && new Money(prepackage?.money_to_pay_for_package).flatFormat()}
                          inputRef={register}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                      </Field>
                    )}
                  </ToggleRow>
                )}

                <OrderSummary
                  classes={classes}
                  translate={translate}
                  currency={currency}
                  origin={origin}
                  destination={destination}
                  sizeLabel={translate(SIZE_CD[sizeCd ?? prepackage?.size_cd ?? 0])}
                  declaredValue={declaredValue}
                  quoteResult={quoteResult}
                />

                {(SHOW_COLLECT_ON_DELIVERY || SHOW_PAY_ON_PICKUP) && (
                  <Box display="flex" alignItems="center" style={{ gap: 8, color: '#666' }}>
                    <MoneyOutlinedIcon fontSize="small" />
                    <Typography variant="caption">{translate('prepackages.form.declaredValueNote')}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
            {quote && (
              <QuoteStrip
                quoteResult={quoteResult}
                translate={translate}
                currency={currency}
                classes={classes}
                disabled={!isValid || quoteResult?.loading}
                onQuote={handleSubmit(quote)}
              />
            )}
            <div className={classes.footer}>
              <Button type="button" startIcon={<ArrowBackIcon />} onClick={() => setExpanded('origin')} style={{ fontWeight: 600 }}>
                {translate('prepackages.form.backToAddresses')}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                disabled={!isValid}
              >
                {isModal ? translate('send') : translate('prepackages.form.create')}
              </Button>
            </div>
          </div>
        )}
      </form>
      {/* Mounted only while open: the modal fetches the favorites as soon as it mounts. */}
      {favoriteTarget && (
        <ModalListFavoriteAddresses
          open
          setOpen={() => setFavoriteTarget(null)}
          cityId={cityId}
          getSelectedAddress={selectFavorite}
        />
      )}
    </>
  )
}

export default withStyles(styles)(PackagesGroupingForm);
